import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import PDFHeader from '../../pdf/PDFHeader';

const styles = StyleSheet.create({
    page: {
        padding: 30,
        fontSize: 10,
    },
    header: {
        marginBottom: 20,
        textAlign: 'center',
        fontSize: 16,
        fontWeight: 'bold',
    },
    subHeader: {
        marginTop: 20,
        marginBottom: 10,
        fontSize: 14,
        fontWeight: 'bold',
    },
    summary: {
        marginBottom: 15,
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    summaryItem: {
        flexDirection: 'column',
    },
    summaryLabel: {
        fontWeight: 'bold',
        marginBottom: 3,
    },
    table: {
        display: "table",
        width: "100%",
        borderStyle: "solid",
        borderWidth: 1,
        borderRightWidth: 0,
        borderBottomWidth: 0,
        marginBottom: 20,
    },
    tableRow: {
        flexDirection: "row",
    },
    tableColHeader: {
        width: "12%",
        borderStyle: "solid",
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 5,
        fontWeight: 'bold',
        backgroundColor: '#f0f0f0',
    },
    tableCol: {
        width: "12%",
        borderStyle: "solid",
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 5,
    },
    footer: {
        marginTop: 20,
        textAlign: 'right',
        fontSize: 10,
        color: 'gray',
    },
    colWide: {
        width: "16%",
    },
    colExtraWide: {
        width: "20%",
    },
    colNarrow: {
        width: "8%",
    }
});

const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US') + ' ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const formatShortDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US');
};

const BillPDFReport = ({ reportData, startDate, endDate, hotelData }) => {
    const userType = localStorage.getItem("billing_app_type");
    const isHotel = userType === "Hotel";
    const isRestaurant = userType === "Restaurant";
    const isHotelAndRestaurant = userType === "Hotel and Restaurant";

    const hotelBills = reportData?.bill_details?.filter(bill => bill.reservation !== null) || [];
    const restaurantBills = reportData?.bill_details?.filter(bill => bill.reservation === null) || [];

    const totalRevenue = isHotelAndRestaurant ?
        (hotelBills.reduce((sum, bill) => sum + (bill.total_amount || 0), 0) +
            restaurantBills.reduce((sum, bill) => sum + (bill.total_amount || 0), 0)) :
        reportData?.total_revenue || 0;

    const totalBills = isHotelAndRestaurant ?
        (hotelBills.length + restaurantBills.length) :
        reportData?.total_bills || 0;

    const filteredBills = isHotel ? hotelBills :
        isRestaurant ? restaurantBills :
            [...hotelBills, ...restaurantBills];

    const renderHotelColumns = () => (
        <>
            <View style={[styles.tableColHeader, styles.colNarrow]}>
                <Text>S.No</Text>
            </View>
            <View style={[styles.tableColHeader, styles.colExtraWide]}>
                <Text>Guest Name</Text>
            </View>
            <View style={styles.tableColHeader}>
                <Text>Phone</Text>
            </View>
            <View style={styles.tableColHeader}>
                <Text>Check In</Text>
            </View>
            <View style={styles.tableColHeader}>
                <Text>Check Out</Text>
            </View>
            <View style={styles.tableColHeader}>
                <Text>Room</Text>
            </View>
            <View style={[styles.tableColHeader, styles.colWide]}>
                <Text>Billing Date</Text>
            </View>
            <View style={styles.tableColHeader}>
                <Text>Status</Text>
            </View>
            <View style={styles.tableColHeader}>
                <Text>Amount</Text>
            </View>
        </>
    );

    const renderRestaurantColumns = () => (
        <>
            <View style={[styles.tableColHeader, styles.colNarrow]}>
                <Text>S.No</Text>
            </View>
            <View style={[styles.tableColHeader, styles.colWide]}>
                <Text>Order ID</Text>
            </View>
            <View style={[styles.tableColHeader, styles.colExtraWide]}>
                <Text>Customer Name</Text>
            </View>
            <View style={styles.tableColHeader}>
                <Text>Phone</Text>
            </View>
            <View style={styles.tableColHeader}>
                <Text>Outlet</Text>
            </View>
            <View style={styles.tableColHeader}>
                <Text>Table</Text>
            </View>
            <View style={[styles.tableColHeader, styles.colWide]}>
                <Text>Date</Text>
            </View>
            <View style={styles.tableColHeader}>
                <Text>Status</Text>
            </View>
            <View style={styles.tableColHeader}>
                <Text>Amount</Text>
            </View>
        </>
    );

    const renderHotelRow = (bill, index) => (
        <View style={styles.tableRow}>
            <View style={[styles.tableCol, styles.colNarrow]}>
                <Text>{index + 1}</Text>
            </View>
            <View style={[styles.tableCol, styles.colExtraWide]}>
                <Text>{bill.reservation?.guests?.name || '-'}</Text>
            </View>
            <View style={styles.tableCol}>
                <Text>{bill.reservation?.guests?.phone || '-'}</Text>
            </View>
            <View style={styles.tableCol}>
                <Text>{formatShortDate(bill.reservation?.check_in)}</Text>
            </View>
            <View style={styles.tableCol}>
                <Text>{formatShortDate(bill.reservation?.check_out)}</Text>
            </View>
            <View style={styles.tableCol}>
                <Text>{bill.reservation?.room?.name || '-'}</Text>
            </View>
            <View style={[styles.tableCol, styles.colWide]}>
                <Text>{formatDisplayDate(bill.created_at)}</Text>
            </View>
            <View style={styles.tableCol}>
                <Text>{bill.status}</Text>
            </View>
            <View style={styles.tableCol}>
                <Text>Rs.{bill.total_amount?.toFixed(2) || '0.00'}</Text>
            </View>
        </View>
    );

    const renderRestaurantRow = (bill, index) => (
        <View style={styles.tableRow}>
            <View style={[styles.tableCol, styles.colNarrow]}>
                <Text>{index + 1}</Text>
            </View>
            <View style={[styles.tableCol, styles.colWide]}>
                <Text>{bill.order_id || '-'}</Text>
            </View>
            <View style={[styles.tableCol, styles.colExtraWide]}>
                <Text>{bill.customer_name || '-'}</Text>
            </View>
            <View style={styles.tableCol}>
                <Text>{bill.customer_phone || '-'}</Text>
            </View>
            <View style={styles.tableCol}>
                <Text>{bill.hotel || '-'}</Text>
            </View>
            <View style={styles.tableCol}>
                <Text>{bill.table || '-'}</Text>
            </View>
            <View style={[styles.tableCol, styles.colWide]}>
                <Text>{formatDisplayDate(bill.created_at)}</Text>
            </View>
            <View style={styles.tableCol}>
                <Text>{bill.status}</Text>
            </View>
            <View style={styles.tableCol}>
                <Text>Rs.{bill.total_amount?.toFixed(2) || '0.00'}</Text>
            </View>
        </View>
    );

    return (
        <Document>
            <Page size="A4" style={styles.page} orientation="landscape">
                {hotelData?.hotel_detail && (
                    <View style={{ marginBottom: 16 }}>
                        <PDFHeader data={hotelData} />
                    </View>
                )}
                <Text style={styles.header}>
                    {isHotelAndRestaurant ? "Hotel & Restaurant" : isHotel ? "Hotel" : "Restaurant"} Bill Report from {formatDisplayDate(startDate)} to {formatDisplayDate(endDate)}
                </Text>

                <View style={styles.summary}>
                    <View style={styles.summaryItem}>
                        <Text style={styles.summaryLabel}>Total Revenue:</Text>
                        <Text>Rs.{totalRevenue.toFixed(2)}</Text>
                    </View>
                    <View style={styles.summaryItem}>
                        <Text style={styles.summaryLabel}>Total Bills:</Text>
                        <Text>{totalBills}</Text>
                    </View>
                    <View style={styles.summaryItem}>
                        <Text style={styles.summaryLabel}>Report Generated:</Text>
                        <Text>{new Date().toLocaleString()}</Text>
                    </View>
                </View>

                {isHotelAndRestaurant ? (
                    <>
                        {hotelBills.length > 0 && (
                            <>
                                <Text style={styles.subHeader}>Hotel Bills</Text>
                                <View style={styles.table}>
                                    <View style={styles.tableRow}>
                                        {renderHotelColumns()}
                                    </View>
                                    {hotelBills.map((bill, index) => renderHotelRow(bill, index))}
                                </View>
                            </>
                        )}
                        {restaurantBills.length > 0 && (
                            <>
                                <Text style={styles.subHeader}>Restaurant Bills</Text>
                                <View style={styles.table}>
                                    <View style={styles.tableRow}>
                                        {renderRestaurantColumns()}
                                    </View>
                                    {restaurantBills.map((bill, index) => renderRestaurantRow(bill, index))}
                                </View>
                            </>
                        )}
                    </>
                ) : (
                    <View style={styles.table}>
                        <View style={styles.tableRow}>
                            {isHotel ? renderHotelColumns() : renderRestaurantColumns()}
                        </View>
                        {filteredBills.map((bill, index) => (
                            isHotel ? renderHotelRow(bill, index) : renderRestaurantRow(bill, index)
                        ))}
                    </View>
                )}
            </Page>
        </Document>
    );
};

export default BillPDFReport;